/**
 * <s-search-suggest> — live search suggestions for the full-width search overlay.
 *
 * Fetches Shopify's native predictive search JSON (data-url, e.g. /search/suggest.json)
 * as the shopper types and renders: a 'Search for "term"' row, then three columns
 * (Suggestions = query terms, Products = thumbnail + name, Pages). Every row is a
 * role="option" with data-ps-url inside #predictive-search-listbox, and the element
 * dispatches a bubbling 's-live-form:updated' event so <s-predictive-search> re-syncs
 * arrow-key navigation and announcements. Products whose handle is in
 * data-hidden-handles (outside the official line-up) are left out.
 */
const DEBOUNCE_MS = 250;

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
}

function sizedImage(url, width) {
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('width', String(width));
    return parsed.toString();
  } catch {
    return url;
  }
}

class SSearchSuggest extends HTMLElement {
  #ac;
  #fetchController;
  #timer;
  #lastTerm = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.input = this.querySelector('#predictive-search-input');
    this.results = this.querySelector('[data-suggest-results]');
    this.initial = this.querySelector('[data-suggest-initial]');
    this.count = this.querySelector('[data-ps-result-count]');
    if (!this.input || !this.results) return;

    this.input.addEventListener('input', () => this.#schedule(), opts);
    this.querySelector('[data-clear-search]')?.addEventListener('click', () => {
      this.input.value = '';
      this.#schedule(0);
      this.input.focus();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#fetchController?.abort();
    clearTimeout(this.#timer);
  }

  #schedule(delay = DEBOUNCE_MS) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#update(), delay);
  }

  async #update() {
    const term = this.input.value.trim();
    if (term === this.#lastTerm) return;
    this.#lastTerm = term;
    this.#fetchController?.abort();

    if (!term) {
      this.#showInitial();
      return;
    }

    this.#fetchController = new AbortController();
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'query,product,page');
    url.searchParams.set('resources[limit]', '8');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(url.toString(), { signal: this.#fetchController.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      const results = data?.resources?.results || {};
      const hidden = new Set((this.dataset.hiddenHandles || '').split(',').filter(Boolean));
      const products = (results.products || []).filter((product) => !hidden.has(product.handle)).slice(0, 6);
      this.#render(term, results.queries || [], products, results.pages || []);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#render(term, [], [], []);
    }
  }

  #showInitial() {
    this.results.hidden = true;
    this.results.innerHTML = '';
    if (this.initial) this.initial.hidden = false;
    if (this.count) this.count.textContent = '';
    this.#notify();
  }

  #render(term, queries, products, pages) {
    const searchUrl = `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}`;
    const safeTerm = escapeHtml(term);
    let index = 0;
    const option = (url, inner) => {
      const id = `ps-option-${index++}`;
      return `<li id="${id}" role="option" aria-selected="false" data-ps-url="${escapeHtml(url)}" class="rounded-xl aria-selected:bg-card"><a href="${escapeHtml(url)}" class="flex items-center gap-3 rounded-xl px-2 py-2 text-heading no-underline hover:bg-card">${inner}</a></li>`;
    };

    const searchFor = (this.dataset.labelSearchFor || 'Search for "[terms]"').replace('[terms]', safeTerm);
    let html = `<ul role="presentation" class="m-0 mb-5 list-none border-b border-border p-0 pb-3">${option(searchUrl, `<span aria-hidden="true">🔍</span><span class="text-base font-semibold">${searchFor}</span>`)}</ul>`;

    const column = (label, items) => `<div role="group" aria-label="${escapeHtml(label)}"><h3 class="mb-2 px-2 text-xs font-bold uppercase tracking-[0.14em] text-accent">${escapeHtml(label)}</h3><ul role="presentation" class="m-0 flex list-none flex-col gap-0.5 p-0">${items}</ul></div>`;

    const queryItems = queries.map((query) => option(query.url || `${this.dataset.searchUrl}?q=${encodeURIComponent(query.text)}`, `<span class="text-sm">${escapeHtml(query.text)}</span>`)).join('');
    const productItems = products.map((product) => {
      const image = product.featured_image?.url || product.image || '';
      const thumb = image
        ? `<img src="${escapeHtml(sizedImage(image, 120))}" alt="" width="56" height="56" loading="lazy" class="size-14 shrink-0 rounded-lg bg-white object-contain">`
        : '<span class="size-14 shrink-0 rounded-lg bg-card"></span>';
      return option(product.url, `${thumb}<span class="line-clamp-2 text-sm font-semibold">${escapeHtml(product.title)}</span>`);
    }).join('');
    const pageItems = pages.map((page) => option(page.url, `<span class="text-sm">${escapeHtml(page.title)}</span>`)).join('');

    const total = queries.length + products.length + pages.length;
    if (total > 0) {
      html += '<div class="grid gap-6 md:grid-cols-[1fr_1.6fr_1fr]">';
      html += queryItems ? column(this.dataset.labelSuggestions, queryItems) : '<div></div>';
      html += productItems ? column(this.dataset.labelProducts, productItems) : '<div></div>';
      html += pageItems ? column(this.dataset.labelPages, pageItems) : '<div></div>';
      html += '</div>';
    } else {
      html += `<p class="py-6 text-center text-sm text-on-surface-secondary">${(this.dataset.labelNoResults || '').replace('[terms]', safeTerm)}</p>`;
    }

    this.results.innerHTML = html;
    this.results.hidden = false;
    if (this.initial) this.initial.hidden = true;
    if (this.count) this.count.textContent = (this.dataset.labelCount || '[count]').replace('[count]', String(total));
    this.#notify();
  }

  #notify() {
    this.dispatchEvent(new CustomEvent('s-live-form:updated', { bubbles: true }));
  }
}

if (!customElements.get('s-search-suggest')) {
  customElements.define('s-search-suggest', SSearchSuggest);
}
